import { AfterViewInit, Component, OnInit, inject, input } from "@angular/core";
import { RouterLink } from "@angular/router";
import { LanguageService } from "src/app/core/services/language.service";
import { Book } from "src/app/models/shared-models/book.model";
import { NavbarColor } from "src/app/models/shared-models/navbar-color";
import { ProductSupply } from "src/app/models/shared-models/product-supply.model";
import {
  SupplyState,
  getSupplyState,
} from "src/app/models/shared-models/product-availability.enum";
import { ProductSupplyFormat } from "src/app/models/shared-models/product-supply-format.enum";
import { LicenseType } from "src/app/models/shared-models/license-type.enum";
import { ProductService } from "src/app/services/product.service";
import { ContributorsGroup } from "./contributors-group.model";
import { SliderItem } from "src/app/models/slider-item.model";
import { SliderItemType } from "src/app/components/slider/slider.component";
import { YumpuDoc } from "src/app/components/yumpu-viewer/yumpu-doc.model";
import { AppImage } from "src/app/models/shared-models/image.model";
import { ScreenSizeService } from "src/app/shared/screen-size.service";
import { IndexabilityService } from "src/app/services/indexability.service";
import { StructuredDataService } from "src/app/services/structured-data.service";
import { SEO } from "src/app/models/shared-models/seo.model";
import { extractAspectRatioFromMeasurement } from "src/app/core/helpers/extractAspectRatioFromMeasurement";
import { TranslateModule } from "@ngx-translate/core";
import { YumpuViewerComponent } from "../../components/yumpu-viewer/yumpu-viewer.component";
import { SimilarBooksComponent } from "./similar-books/similar-books.component";
import { AccordionContent } from "../../shared/accordion/directives/accordion-content.directive";
import { AccordionItem } from "../../shared/accordion/directives/accordion-item.directive";
import { AccordionComponent } from "../../shared/accordion/accordion.component";
import { ShowAboveBelowDirective } from "../../shared/show-above-below.directive";
import { BlankTargetLinksDirective } from "@app/shared/blank-target-links.directive";
import { ProductSupplyInfoComponent } from "./product-supply-info/product-supply-info.component";
import { BuyButtonComponent } from "./buy-button/buy-button.component";
import { SliderComponent } from "../../components/slider/slider.component";
import { BackComponent } from "../../components/back/back.component";
import { InlineSVGModule } from "ng-inline-svg-w";
import { SpinnerDotsComponent } from "@app/components/spinner-dots/spinner-dots.component";
import { NavbarColorDirective } from "../../shared/navbar-color.directive";
import { NgTemplateOutlet } from "@angular/common";
import { signal } from "@angular/core";
import { ProductCompactComponent } from "@app/components/shop/product-compact/product-compact.component";

enum PageState {
  LOADING,
  LOADED,
  ERROR,
  NOT_FOUND,
}

interface RelatedTitle {
  isbn: string;
  language: string[];
}

export const navbarColor: NavbarColor = {
  background: "#BAC6CB",
  text: "#1c1c1c",
};

export const detailsNavBarColor: NavbarColor = {
  background: "#D0D8DB",
  text: "#1c1c1c",
};

@Component({
  selector: "app-book",
  templateUrl: "./book.component.html",
  styleUrls: ["./book.component.scss"],
  standalone: true,
  imports: [
    NavbarColorDirective,
    SpinnerDotsComponent,
    RouterLink,
    InlineSVGModule,
    BackComponent,
    SliderComponent,
    BuyButtonComponent,
    ProductCompactComponent,
    ProductSupplyInfoComponent,
    ShowAboveBelowDirective,
    BlankTargetLinksDirective,
    NgTemplateOutlet,
    AccordionComponent,
    AccordionItem,
    AccordionContent,
    SimilarBooksComponent,
    YumpuViewerComponent,
    TranslateModule,
  ],
})
export class BookComponent implements OnInit, AfterViewInit {
  productService = inject(ProductService);
  private languageService = inject(LanguageService);
  private screenSizeService = inject(ScreenSizeService);
  private indexabilityService = inject(IndexabilityService);
  private structuredDataService = inject(StructuredDataService);

  state = signal<PageState>(PageState.LOADED);
  PageState = PageState;
  book = input<Book | undefined>(undefined);
  yumpuDoc = signal<YumpuDoc | undefined>(undefined);
  slides = signal<SliderItemType[]>([]);
  sortedProductSupply = signal<ProductSupply[]>([]);
  relatedTitles = signal<RelatedTitle[]>([]);
  setMembers = signal<Book[] | undefined>(undefined);
  isMobile = this.screenSizeService.isMobile();

  navbarColor: NavbarColor = {
    background: "#BAC6CB",
    text: "#1c1c1c",
  };

  detailsNavBarColor: NavbarColor = {
    background: "#D0D8DB",
    text: "#1c1c1c",
  };

  async ngOnInit() {
    const book = this.book();
    if (!book) return;

    const seo: SEO = this.getSeoData(book);
    const setIds = this.book()?.bookData?.setMembers;
    this.indexabilityService.setMetaTags(seo);
    this.structuredDataService.addStructuredData(book);

    this.setSortedProductSupply(book);
    await this.setRelatedISBNs();
    await this.getBookImages(book);

    if (setIds) {
      const members = await this.productService.getBooks(setIds);
      this.setMembers.set(members);
    }
  }

  async ngAfterViewInit() {
    const yumpuDoc = await this.productService.getYumpuDoc(
      this.book()?.productSupply?.[0]?.isbn || "",
    );
    this.yumpuDoc.set(yumpuDoc);
  }

  get currentLang(): string {
    return this.languageService.currentLanguage();
  }

  getContributorGroups(): ContributorsGroup[] {
    const book = this.book();
    if (!book) return [];
    return this.productService.getContributorGroups(book);
  }

  getSeoData(book: Book): SEO {
    const currentLang = this.languageService.currentLanguage();

    return {
      title: book.title,
      description: book?.bookData?.description?.[currentLang] ?? "",
      relativeUrl: `book/${book?.productSupply?.[0]?.isbn}`,
    };
  }

  setSortedProductSupply(book: Book) {
    // print first, ebook second
    if (!book.productSupply) return;

    // Hide ebook entries with no purchasable price (e.g. EBA-only / chunk pricing
    // that gets filtered out upstream and arrives here with an empty `prices` array).
    // Open Access ebooks are exempt — they have no price by design and render a
    // free-download button instead of a buy button.
    const isEbookWithMissingPrice = (supply: ProductSupply): boolean =>
      supply.format === ProductSupplyFormat.Electronic &&
      supply.license !== LicenseType.OpenAccess &&
      (!supply.prices || supply.prices.length === 0);

    const purchasableSupply = book.productSupply.filter(
      (s) => !isEbookWithMissingPrice(s),
    );

    const sorted = purchasableSupply
      .sort((a, b) => {
        // unpublished entries always go last, regardless of format
        const aUnpublished = this.isUnpublished(a);
        const bUnpublished = this.isUnpublished(b);

        if (aUnpublished !== bUnpublished) {
          return aUnpublished ? 1 : -1;
        }

        // both published (or both unpublished): sort by format, electronic first
        const aElectronic = a.format === ProductSupplyFormat.Electronic;
        const bElectronic = b.format === ProductSupplyFormat.Electronic;
        if (aElectronic !== bElectronic) {
          return aElectronic ? -1 : 1;
        }

        return 0;
      })
      .map((s) => ({ ...s, language: book.language?.[0] }));

    this.sortedProductSupply.set(sorted);
  }

  /**
   * Purchase state of one supply, read the way the buy button reads it: the
   * availability code gates sellability, `plannedPublishingDate` gates
   * release.
   */
  private supplyState(productSupply: ProductSupply): SupplyState {
    return getSupplyState({
      availability: productSupply.availability,
      plannedPublishingDate: productSupply.plannedPublishingDate,
      weeksUntilPlannedPublishing:
        this.weeksUntilPlannedPublishing(productSupply),
    });
  }

  /**
   * Weeks until the announced publication date, `undefined` when there is
   * none to read. The service throws on a missing date and answers NaN on one
   * it cannot read, and `getSupplyState` takes any number for a readable date.
   */
  private weeksUntilPlannedPublishing(
    productSupply: ProductSupply,
  ): number | undefined {
    try {
      const weeks = this.productService.getPublishingDateWeeksDifference(
        productSupply.plannedPublishingDate ?? undefined,
      );
      return Number.isFinite(weeks) ? weeks : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * True when a supply has nothing to sell yet. The planned publication date
   * decides it: the publication delivery drops the element, so a published
   * supply keeps its buy button in front of the reader however far ahead its
   * ONIX date sits. A supply with no planned date — and an out-of-print or
   * out-of-stock one, whose code says nothing about publication — keeps the
   * date test the page ran before, throw included.
   */
  private isUnpublished(productSupply: ProductSupply): boolean {
    const state = this.supplyState(productSupply);
    if (state === SupplyState.Preorder || state === SupplyState.Upcoming) {
      return true;
    }
    if (state === SupplyState.Buy) return false;

    return (
      this.productService.getPublishingDateWeeksDifference(
        productSupply.publishingDate,
      ) > 0
    );
  }

  getDescription(): string {
    return this.languageService.getLocalizedInfo(
      this.book()?.bookData?.description,
    );
  }

  getKeywords(): string[] {
    return this.languageService.getLocalizedInfoArray(this.book()?.keywords);
  }

  getAuthorInfo(): string {
    return this.languageService.getLocalizedInfo(
      this.book()?.bookData?.authorInfo,
    );
  }

  getPressLink(): string {
    let url = "";
    const lang = this.languageService.currentLanguage();
    url = `/${lang}/publisher/press`;
    const params = new URLSearchParams();
    params.set("isbn", this.book()?.productSupply?.[0]?.isbn || "");
    params.set("title", this.book()?.title || "");

    url += "?" + params.toString();
    return url;
  }

  isAuthorOrEditor(contributorRole: string): boolean {
    return contributorRole === "A01" || contributorRole === "B01";
  }

  showAccordion(): boolean {
    const hasDescription = !!this.book()?.bookData?.description;
    const hasAuthorInfo = !!this.book()?.bookData?.authorInfo;
    const hasReviews = !!this.book()?.bookData?.reviews?.length;
    if (this.screenSizeService.isMobile()) {
      return hasDescription || hasAuthorInfo || hasReviews;
    } else {
      return hasAuthorInfo || hasReviews;
    }
  }

  async setRelatedISBNs() {
    // TODO: improve this by adding the related titles on the second stage of the parser

    const relatedISBNs = this.book()?.bookData?.relatedISBNs;
    if (!relatedISBNs) return;

    const relatedTitles: RelatedTitle[] = [];
    for (const isbn of relatedISBNs) {
      try {
        const book = await this.productService.getBook(isbn);
        if (book) {
          relatedTitles.push({
            isbn: isbn,
            language: book.language,
          });
        }
      } catch (error) {
        console.error(error);
      }
    }

    this.relatedTitles.set(relatedTitles);
  }

  async getBookImages(book: Book) {
    const allIsbns = book.productSupply?.map((ps) => ps.isbn) ?? [];

    const results = await Promise.allSettled(
      allIsbns.map((isbn) => this.productService.getAdditionalImages(isbn)),
    );

    const additionalImages = results
      .filter(
        (r): r is PromiseFulfilledResult<AppImage[]> =>
          r.status === "fulfilled" && !!r.value,
      )
      .flatMap((r) => r.value)
      .filter((image, index, self) => {
        const url = image.urls[1] ?? image.urls[0];
        return (
          index ===
          self.findIndex((img) => (img.urls[1] ?? img.urls[0]) === url)
        );
      });

    const cover = book.covers[0];

    if (cover) {
      this.slides.set([cover, ...additionalImages]);
    } else {
      const placeholder: AppImage = {
        urls: [],
        blurHash: "",
        aspectRatio:
          extractAspectRatioFromMeasurement(
            book.productSupply?.[0]?.measurement,
          ) ?? 3 / 4,
      };
      this.slides.set([placeholder]);
    }
  }

  hasCover(): boolean {
    const book = this.book();
    if (!book) return false;
    return book.covers && book.covers.length > 0;
  }

  async downloadCover() {
    const cover = this.book()?.covers[0];
    const url = cover?.urls[1] ?? cover?.urls[0];
    if (!url) return;
    try {
      // Create a more specific filename with file extension
      const fileName = `${this.book()?.title}_Cover.jpg`;
      // Fetch the image
      const response = await fetch(url);
      const blob = await response.blob();
      // Create a download link
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none"; // Hide the element
      a.href = blobUrl;
      a.download = fileName;
      // Append to body, trigger download, and remove
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Clean up the blob URL after download starts
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading cover:", error);
    }
  }

  getRelatedISBNLink(relatedTitle: RelatedTitle): string {
    const lang = this.languageService.currentLanguage();
    return `${lang}/book/` + relatedTitle.isbn;
  }

  trackByProductSupply(index: number, item: ProductSupply): string {
    return item.isbn;
  }

  trackBySeries(index: number, item: any): string {
    return item.seriesKey;
  }

  trackByContributorGroup(index: number, item: any): string {
    return `${item.contributorRole}-${item.names.join("-")}`;
  }

  trackByRelatedTitle(index: number, item: any): string {
    return item.isbn;
  }

  trackByKeyword(index: number, item: string): string {
    return item;
  }

  isBookSet(): boolean {
    return (
      this.book()?.productSupply?.some((supply) => supply.format === "Set") ??
      false
    );
  }
}
